import { useAuth } from '../hooks/useAuth';

// ─── Role type ────────────────────────────────────────────────────────────────

export type UserRole =
  | 'admin'
  | 'project-manager'
  | 'estimator'
  | 'site-engineer'
  | 'site-supervisor'
  | 'site-worker'
  | 'procurement-officer'
  | 'finance-manager'
  | 'accountant'
  | 'safety-officer'
  | 'viewer';

export const ALL_ROLES: UserRole[] = [
  'admin', 'project-manager', 'estimator', 'site-engineer', 'site-supervisor',
  'site-worker', 'procurement-officer', 'finance-manager', 'accountant',
  'safety-officer', 'viewer',
];

// ─── Route permission matrix ──────────────────────────────────────────────────

export type RoutePermission = {
  view: UserRole[];
  write: UserRole[];
  approve: UserRole[];
  delete: UserRole[];
};

export const ROUTE_PERMISSIONS: Record<string, RoutePermission> = {
  '/dashboard': {
    view: ALL_ROLES,
    write: [],
    approve: [],
    delete: [],
  },

  // Pre-Construction & Planning
  '/boq': {
    view: ['admin', 'project-manager', 'estimator', 'finance-manager', 'viewer'],
    write: ['admin', 'project-manager', 'estimator'],
    approve: ['admin', 'project-manager'],
    delete: ['admin', 'project-manager', 'estimator'],
  },
  '/estimates': {
    view: ['admin', 'project-manager', 'estimator', 'finance-manager', 'viewer'],
    write: ['admin', 'project-manager', 'estimator'],
    approve: ['admin', 'project-manager', 'finance-manager'],
    delete: ['admin', 'project-manager', 'estimator'],
  },
  '/bids': {
    view: ['admin', 'project-manager', 'estimator', 'finance-manager', 'viewer'],
    write: ['admin', 'project-manager', 'estimator'],
    approve: ['admin', 'project-manager'],
    delete: ['admin', 'project-manager', 'estimator'],
  },

  // Project Management
  '/projects': {
    view: ['admin', 'project-manager', 'estimator', 'site-engineer', 'site-supervisor',
           'procurement-officer', 'finance-manager', 'accountant', 'safety-officer', 'viewer'],
    write: ['admin', 'project-manager'],
    approve: [],
    delete: ['admin'],
  },
  '/projects/detail': {
    view: ['admin', 'project-manager', 'estimator', 'site-engineer', 'site-supervisor',
           'procurement-officer', 'finance-manager', 'accountant', 'safety-officer', 'viewer'],
    write: ['admin', 'project-manager'],
    approve: [],
    delete: ['admin'],
  },
  '/schedules': {
    view: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'site-worker', 'viewer'],
    write: ['admin', 'project-manager'],
    approve: ['admin', 'project-manager'],
    delete: ['admin', 'project-manager'],
  },
  '/daily-logs': {
    view: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'site-worker', 'safety-officer', 'viewer'],
    write: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'site-worker'],
    approve: [],
    delete: ['admin', 'project-manager'],
  },
  '/documents': {
    view: ALL_ROLES,
    write: ['admin', 'project-manager', 'site-engineer', 'site-supervisor'],
    approve: [],
    delete: ['admin', 'project-manager'],
  },
  '/rfis': {
    view: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'viewer'],
    write: ['admin', 'project-manager', 'site-engineer', 'site-supervisor'],
    approve: ['admin', 'project-manager'],
    delete: ['admin', 'project-manager'],
  },
  '/submittals': {
    view: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'viewer'],
    write: ['admin', 'project-manager', 'site-engineer', 'site-supervisor'],
    approve: ['admin', 'project-manager'],
    delete: ['admin', 'project-manager'],
  },
  '/inspections': {
    view: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'safety-officer', 'viewer'],
    write: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'safety-officer'],
    approve: ['admin', 'project-manager', 'site-supervisor', 'safety-officer'],
    delete: ['admin', 'project-manager'],
  },
  '/safety-incidents': {
    view: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'site-worker', 'safety-officer', 'viewer'],
    write: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'site-worker', 'safety-officer'],
    approve: ['admin', 'project-manager', 'safety-officer'],
    delete: ['admin', 'project-manager', 'safety-officer'],
  },
  '/punch-lists': {
    view: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'safety-officer', 'viewer'],
    write: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'safety-officer'],
    approve: ['admin', 'project-manager', 'site-supervisor'],
    delete: ['admin', 'project-manager'],
  },

  // Procurement & Inventory
  '/suppliers': {
    view: ['admin', 'procurement-officer', 'finance-manager', 'viewer'],
    write: ['admin', 'procurement-officer'],
    approve: [],
    delete: ['admin', 'procurement-officer'],
  },
  '/purchase-orders': {
    view: ['admin', 'project-manager', 'procurement-officer', 'finance-manager', 'viewer'],
    write: ['admin', 'project-manager', 'procurement-officer'],
    approve: ['admin', 'finance-manager'],
    delete: ['admin', 'procurement-officer'],
  },
  '/goods-received': {
    view: ['admin', 'project-manager', 'procurement-officer', 'site-supervisor', 'viewer'],
    write: ['admin', 'procurement-officer', 'site-supervisor'],
    approve: [],
    delete: ['admin', 'procurement-officer'],
  },
  '/inventory': {
    view: ['admin', 'project-manager', 'procurement-officer', 'site-engineer', 'site-supervisor', 'viewer'],
    write: ['admin', 'procurement-officer'],
    approve: [],
    delete: ['admin', 'procurement-officer'],
  },
  '/material-requisitions': {
    view: ['admin', 'project-manager', 'procurement-officer', 'site-engineer', 'site-supervisor', 'site-worker', 'viewer'],
    write: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'site-worker'],
    approve: ['admin', 'project-manager', 'procurement-officer', 'site-supervisor'],
    delete: ['admin', 'project-manager', 'procurement-officer'],
  },

  // Financials & Contracts
  '/budget-cost': {
    view: ['admin', 'project-manager', 'finance-manager', 'accountant', 'viewer'],
    write: ['admin', 'project-manager', 'finance-manager'],
    approve: ['admin', 'finance-manager'],
    delete: ['admin', 'finance-manager'],
  },
  '/contracts': {
    view: ['admin', 'project-manager', 'finance-manager', 'accountant', 'viewer'],
    write: ['admin', 'project-manager', 'finance-manager'],
    approve: ['admin', 'finance-manager'],
    delete: ['admin', 'finance-manager'],
  },
  '/change-orders': {
    view: ['admin', 'project-manager', 'finance-manager', 'accountant', 'viewer'],
    write: ['admin', 'project-manager'],
    approve: ['admin', 'project-manager', 'finance-manager'],
    delete: ['admin', 'project-manager'],
  },
  '/invoices-ap': {
    view: ['admin', 'finance-manager', 'accountant', 'viewer'],
    write: ['admin', 'finance-manager', 'accountant'],
    approve: ['admin', 'finance-manager'],
    delete: ['admin', 'finance-manager'],
  },
  '/payment-applications-ar': {
    view: ['admin', 'finance-manager', 'accountant', 'viewer'],
    write: ['admin', 'finance-manager', 'accountant'],
    approve: ['admin', 'finance-manager'],
    delete: ['admin', 'finance-manager'],
  },
  '/payments': {
    view: ['admin', 'finance-manager', 'accountant', 'viewer'],
    write: ['admin', 'finance-manager'],
    approve: ['admin', 'finance-manager'],
    delete: ['admin', 'finance-manager'],
  },
  '/timesheets': {
    view: ['admin', 'project-manager', 'finance-manager', 'accountant', 'site-engineer', 'site-supervisor', 'site-worker', 'viewer'],
    write: ['admin', 'project-manager', 'site-engineer', 'site-supervisor', 'site-worker'],
    approve: ['admin', 'project-manager', 'site-supervisor', 'finance-manager'],
    delete: ['admin', 'project-manager'],
  },
  '/cost-codes': {
    view: ['admin', 'project-manager', 'estimator', 'procurement-officer', 'finance-manager', 'accountant', 'viewer'],
    write: ['admin', 'project-manager', 'finance-manager'],
    approve: [],
    delete: ['admin', 'project-manager', 'finance-manager'],
  },

  // Reports
  '/reports': {
    view: ['admin', 'project-manager', 'estimator', 'site-supervisor', 'procurement-officer', 'finance-manager', 'accountant', 'safety-officer', 'viewer'],
    write: [],
    approve: [],
    delete: [],
  },

  // Notifications — every authenticated user sees their own feed
  '/notifications': {
    view: ALL_ROLES,
    write: ALL_ROLES,
    approve: [],
    delete: [],
  },

  // Settings
  '/settings/profile': {
    view: ALL_ROLES,    // every user can view and edit their own profile
    write: ALL_ROLES,
    approve: [],
    delete: [],
  },
  '/settings/organization': {
    view: ['admin'],    // org-wide settings: admin only
    write: ['admin'],
    approve: [],
    delete: [],
  },
  '/settings/users': {
    view: ['admin'],    // user management: admin only
    write: ['admin'],
    approve: [],
    delete: ['admin'],
  },
};

// ─── Action gates ──────────────────────────────────────────────────────────────

export type ActionKey =
  | 'create_project'
  | 'approve_estimate'
  | 'approve_po'
  | 'send_po_to_vendor'
  | 'receive_goods'
  | 'approve_requisition'
  | 'approve_invoice'
  | 'approve_payment'
  | 'approve_timesheet'
  | 'approve_change_order'
  | 'complete_inspection'
  | 'close_safety_incident'
  | 'assign_user_role'
  | 'deactivate_user';

export const ACTION_ROLES: Record<ActionKey, UserRole[]> = {
  create_project:        ['admin', 'project-manager'],
  approve_estimate:      ['admin', 'project-manager', 'finance-manager'],
  approve_po:            ['admin', 'finance-manager'],
  send_po_to_vendor:     ['admin', 'procurement-officer'],
  receive_goods:         ['admin', 'procurement-officer', 'site-supervisor'],
  approve_requisition:   ['admin', 'project-manager', 'procurement-officer', 'site-supervisor'],
  approve_invoice:       ['admin', 'finance-manager'],
  approve_payment:       ['admin', 'finance-manager'],
  approve_timesheet:     ['admin', 'project-manager', 'site-supervisor', 'finance-manager'],
  approve_change_order:  ['admin', 'project-manager', 'finance-manager'],
  complete_inspection:   ['admin', 'project-manager', 'site-supervisor', 'safety-officer'],
  close_safety_incident: ['admin', 'project-manager', 'safety-officer'],
  assign_user_role:      ['admin'],
  deactivate_user:       ['admin'],
};

// ─── Core helpers ─────────────────────────────────────────────────────────────

/** Returns the user's effective roles from their profile (primary + additional). */
export function getUserRoles(profileRoles: string[]): UserRole[] {
  return profileRoles.filter((r): r is UserRole =>
    (ALL_ROLES as string[]).includes(r)
  );
}

function intersects(userRoles: UserRole[], allowed: UserRole[]): boolean {
  return userRoles.some((r) => allowed.includes(r));
}

export function canAccessRoute(userRoles: UserRole[], route: string): boolean {
  const perm = ROUTE_PERMISSIONS[route];
  if (!perm) return false;
  return intersects(userRoles, perm.view);
}

export function canWrite(userRoles: UserRole[], route: string): boolean {
  return intersects(userRoles, ROUTE_PERMISSIONS[route]?.write ?? []);
}

export function canApprove(userRoles: UserRole[], route: string): boolean {
  return intersects(userRoles, ROUTE_PERMISSIONS[route]?.approve ?? []);
}

export function canDelete(userRoles: UserRole[], route: string): boolean {
  return intersects(userRoles, ROUTE_PERMISSIONS[route]?.delete ?? []);
}

export function canDo(userRoles: UserRole[], action: ActionKey): boolean {
  return intersects(userRoles, ACTION_ROLES[action]);
}

// ─── React hook ───────────────────────────────────────────────────────────────

export function usePermission() {
  const { user } = useAuth();
  const roles = getUserRoles(user?.roles ?? []);

  return {
    roles,
    canAccessRoute: (route: string) => canAccessRoute(roles, route),
    canWrite:  (route: string) => canWrite(roles, route),
    canApprove:(route: string) => canApprove(roles, route),
    canDelete: (route: string) => canDelete(roles, route),
    canDo:     (action: ActionKey) => canDo(roles, action),
    isAdmin:   roles.includes('admin'),
  };
}
